import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Header from './components/Layout/Header';
import Navigation from './components/Layout/Navigation';
import ReservationManagement from './components/Reservations/ReservationManagement';
import ReservationList from './components/Reservations/ReservationList';
import SeasonalManagement from './components/Seasonal/SeasonalManagement';
import HeroManagement from './components/Hero/HeroManagement';
import MenuManagement from './components/Menu/MenuManagement';
import KodawariManagement from './components/Kodawari/KodawariManagement';
import StorePhotosManagement from './components/StorePhotos/StorePhotosManagement';
import BusinessHours from './components/Settings/BusinessHours';
import Holidays from './components/Settings/Holidays';
import ReservationToggle from './components/Settings/ReservationToggle';
import Analytics from './components/Analytics/Analytics';
import LoginButton from './components/Auth/LoginButton';
import LoadingSpinner from './components/Common/LoadingSpinner';
import { setSupabaseTokenGetter } from './services/supabase';
import './App.css';
import './admin.css';

// Auth0を使用するメインアプリコンポーネント
function AuthenticatedApp() {
  const { isAuthenticated, isLoading, getIdTokenClaims, getAccessTokenSilently } = useAuth0();
  const [activeTab, setActiveTab] = React.useState('reservations');

  // Supabase へのリクエストに付ける Auth0 の ID トークンを取得する関数を登録する。
  // 子コンポーネントのデータ取得より先に登録されるよう、描画中に設定する（何度設定しても同じ）。
  setSupabaseTokenGetter(async () => {
    let claims = await getIdTokenClaims();
    // 未取得、または期限切れ間近なら、トークンを更新してから取り直す
    if (!claims || claims.exp * 1000 < Date.now() + 60 * 1000) {
      await getAccessTokenSilently({ cacheMode: 'off' });
      claims = await getIdTokenClaims();
    }
    return claims ? claims.__raw : null;
  });

  if (isLoading) {
    return (
      <div className="app-loading">
        <LoadingSpinner />
        <p>読み込み中...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app-login">
        <div className="login-container">
          <h1>よし鮨 管理者画面</h1>
          <p>管理者としてログインしてください</p>
          <LoginButton />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header />
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="admin-main">
        {activeTab === 'reservations' && <ReservationManagement />}
        {activeTab === 'reservations-list' && <ReservationList />}
        {activeTab === 'hero' && <HeroManagement />}
        {activeTab === 'menu' && <MenuManagement />}
        {activeTab === 'kodawari' && <KodawariManagement />}
        {activeTab === 'seasonal' && <SeasonalManagement />}
        {activeTab === 'store-photos' && <StorePhotosManagement />}
        {activeTab === 'settings' && (
          <>
            <BusinessHours />
            <Holidays />
          </>
        )}
        {activeTab === 'reservation-toggle' && <ReservationToggle />}
        {activeTab === 'analytics' && <Analytics />}
      </main>
    </div>
  );
}

// Auth0をスキップするアプリコンポーネント（開発用/Auth0未設定時）
function UnauthenticatedApp() {
  const [activeTab, setActiveTab] = React.useState('reservations');

  return (
    <div className="app">
      <Header skipAuth={true} />
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="admin-main">
        {activeTab === 'reservations' && <ReservationManagement />}
        {activeTab === 'reservations-list' && <ReservationList />}
        {activeTab === 'hero' && <HeroManagement />}
        {activeTab === 'menu' && <MenuManagement />}
        {activeTab === 'kodawari' && <KodawariManagement />}
        {activeTab === 'seasonal' && <SeasonalManagement />}
        {activeTab === 'store-photos' && <StorePhotosManagement />}
        {activeTab === 'settings' && (
          <>
            <BusinessHours />
            <Holidays />
          </>
        )}
        {activeTab === 'reservation-toggle' && <ReservationToggle />}
        {activeTab === 'analytics' && <Analytics />}
      </main>
    </div>
  );
}

// メインのAppコンポーネント
function App({ skipAuth = false }) {
  if (skipAuth) {
    return <UnauthenticatedApp />;
  }
  return <AuthenticatedApp />;
}

export default App;

